import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash";
import { usePreferences } from "./usePreferences";
import { preferenceQueryKeys } from "../api/usePreferenceQuery";
import useApi from "../../../../utils/api";
import { useCompany } from "../../../../utils/hooks/company";
import { normalize } from "../../../../utils/helpers/normalize";

const formatPreferences = (data: any) => {
    return (data || [])?.reduce((total: any, item: any) => ({
        ...total,
        [item.preferenceKey]: item.config,
    }), {});
};

export const useColumnsPreferences = (columns: any, feature: string) => {
    const company = useCompany();
    const { authGet } = useApi();

    // Subscribe reactively to company prefs — company controls hideCompletely and hideSort
    const { data: rawCompanyData } = useQuery<any>({
        queryKey: preferenceQueryKeys.company(company?.id as string),
        queryFn: () => authGet(`/preferencies/company/${company?.id}`),
        enabled: !!company?.id,
    });

    const rawConfigColumns = usePreferences(feature);
    const configColumns = Array.isArray(rawConfigColumns) ? rawConfigColumns : rawConfigColumns?.columns;

    const companyMap = useMemo(() => {
        const rawCompanyColumns = get(formatPreferences(rawCompanyData?.result), feature);
        const companyColumns = Array.isArray(rawCompanyColumns) ? rawCompanyColumns : rawCompanyColumns?.columns;
        return normalize(Array.isArray(companyColumns) ? companyColumns : [], "accessor");
    }, [rawCompanyData, feature]);

    const map = useMemo(
        () => normalize(Array.isArray(configColumns) ? configColumns : [], "accessor"),
        [configColumns]
    );

    return useMemo(() => {
        return (columns || [])?.reduce((acc: any, column: any) => {
            const key =
                "accessorKey" in column
                    ? String(column.accessorKey)
                    : column.id ?? (typeof column.accessor === "string" ? column.accessor : undefined);

            if (!key) {
                acc.push(column);
                return acc;
            }

            const config = map[key];
            const companyConfig = companyMap[key];

            // Company always controls hideCompletely and hideSort regardless of user prefs
            if (companyConfig?.hideCompletely || config?.hideCompletely) return acc;

            acc.push({
                ...column,
                enableSorting: (companyConfig?.hideSort || config?.hideSort) ? false : column.enableSorting,
            });

            return acc;
        }, []);
    }, [columns, map, companyMap]);
};
